import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, NavigationExtras, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

import { AuthService as AdminAuthService } from '@app/modules/admin/auth/services/auth.service';

import { CoreComponent } from '../core.component';

import { JwtHelperService } from '@auth0/angular-jwt';

import { IMagicLogin } from '@lib/auth-token.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard extends CoreComponent implements CanActivate {
  private helper = new JwtHelperService();

  constructor(
    private router: Router,
    private adminAuthService: AdminAuthService,
  ) {
    super();
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const token: boolean = !!localStorage.getItem('token');
    const magicToken: string = route.queryParams['magic-token'];
    const data: { token: string } = { token: magicToken };
    const tree: UrlTree = this.router.parseUrl(state.url);
    delete tree.queryParams['magic-token'];
    const returnUrl: string = tree.toString();
    const queryParams = (state.url === '/dashboard') ? {} : { queryParams: { returnUrl: returnUrl } };
    const isTokenExpired: boolean = this.helper.isTokenExpired(this.adminAuthService.token);

    return of(token)
      .pipe(
        takeUntil(this.destroyed$),
        switchMap((response: boolean): Observable<boolean | UrlTree> => {
          if (response && !isTokenExpired) return of(route.queryParams['magic-token'] ? tree : true);
          else if (!!magicToken) {
            return this.adminAuthService.magicLogin(data)
              .pipe(
                tap((response: IMagicLogin): void => {
                  // With a challenge there is no session yet: the login page completes it with the code.
                  if (!response.challengeToken) {
                    this.adminAuthService.token = response.token;
                  }
                  if (response.isAdmin2fa) localStorage.setItem('magicTokenResponse', JSON.stringify(response));
                }),
                map((response: IMagicLogin): boolean | UrlTree => {
                  // The login page completes the challenge with the code; no session exists yet.
                  if (response.challengeToken) {
                    return this.router.createUrlTree(['/login'], queryParams);
                  }
                  if (response.isAdmin2fa) return route.queryParams['magic-token'] ? tree : true;
                  else {
                    this.unAuthorizedUser(queryParams);
                    return false;
                  }
                }),
                catchError(() => this.unAuthorizedUser(queryParams)),
              );
          }
          else return this.unAuthorizedUser(queryParams);
        }),
        catchError(() => this.unAuthorizedUser(queryParams)),
      )
  }

  private unAuthorizedUser(queryParams: NavigationExtras): Observable<boolean> {
    this.adminAuthService.logout(queryParams);
    return of(false);
  }
}
